import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import React, { useState } from "react";
import { toast } from "sonner";

import { saveKontragentMapping } from "@/api/mappings";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { OneCRef } from "@/types/api";
import { useConnection } from "@/auth/ConnectionContext";

interface Props {
  open: boolean;
  sourceInn: string;
  sourceKpp: string | null;
  sourceName: string;
  suggestions: OneCRef[];
  invoiceId: string;
  onClose: () => void;
}

export function KontragentPicker({
  open,
  sourceInn,
  sourceKpp,
  sourceName,
  suggestions,
  invoiceId,
  onClose,
}: Props): React.ReactElement {
  const queryClient = useQueryClient();
  const { activeConnectionId: connId } = useConnection();
  const [selected, setSelected] = useState<OneCRef | null>(null);

  const mutation = useMutation({
    mutationFn: saveKontragentMapping,
    onSuccess: () => {
      toast.success("Соответствие сохранено");
      queryClient.invalidateQueries({ queryKey: ["invoices", invoiceId, "preview", connId] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof AxiosError) {
        toast.error(err.response?.data?.detail ?? "Не удалось сохранить");
      } else {
        toast.error("Не удалось сохранить");
      }
    },
  });

  const handleConfirm = () => {
    if (!selected) return;
    mutation.mutate({
      source_inn: sourceInn,
      source_kpp: sourceKpp,
      one_c_kontragent_key: selected.ref_key,
      one_c_kontragent_name: selected.name,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Выберите контрагента в 1С</DialogTitle>
          <DialogDescription>
            Для контрагента из документа: <b>{sourceName}</b>
            {sourceInn && (
              <span className="text-muted-foreground">
                {" "}
                (ИНН {sourceInn}
                {sourceKpp ? ` / КПП ${sourceKpp}` : ""})
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {suggestions.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            В 1С не найдено ни одного варианта. Добавьте контрагента в 1С и
            обновите страницу.
          </p>
        ) : (
          <ul className="max-h-80 space-y-2 overflow-y-auto">
            {suggestions.map((s) => {
              const isSelected = selected?.ref_key === s.ref_key;
              return (
                <li key={s.ref_key}>
                  <button
                    type="button"
                    onClick={() => setSelected(s)}
                    className={
                      "w-full rounded-md border p-3 text-left text-sm transition-colors " +
                      (isSelected
                        ? "border-primary bg-primary/5"
                        : "hover:bg-accent/50")
                    }
                  >
                    <div className="font-medium">{s.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {s.inn && <>ИНН {s.inn}</>}
                      {s.kpp && <> / КПП {s.kpp}</>}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selected || mutation.isPending}
          >
            {mutation.isPending ? "Сохранение…" : "Сохранить выбор"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
